import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Container,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

const guidelinesData = {
  Introduction: (
    <>
      <Typography paragraph>
        OnlyHands is a fitness-focused platform designed to empower trainers,
        fitness professionals, and enthusiasts to share and monetize their
        content responsibly. Our goal is to foster a safe, professional, and
        engaging environment where users can access high-quality fitness content
        while adhering to ethical standards and legal requirements.
      </Typography>
      <Typography paragraph>
        These Community Guidelines apply to all users — both creators and
        subscribers — and outline what is permitted and prohibited on OnlyHands.
        Violations of these guidelines may result in content removal, account
        suspension, or permanent bans.
      </Typography>
    </>
  ),
  "Policy Transparency & Community Engagement": (
    <List>
      <ListItem>
        <ListItemText primary="OnlyHands values transparency in its policy enforcement and seeks community engagement on major changes." />
      </ListItem>
      <ListItem>
        <ListItemText primary="We will communicate policy updates and seek creator feedback before major revisions." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Users can contact our Policy Team at onlyhandsdev@gmail.com for questions, concerns, or suggestions regarding policy enforcement." />
      </ListItem>
    </List>
  ),
  "Professional Conduct & Respect": (
    <>
      <Typography paragraph>
        OnlyHands is a professional platform where users must maintain
        respectful interactions at all times.
      </Typography>
      <Typography variant="h6">2.1 Harassment & Bullying</Typography>
      <List>
        <ListItem>
          <ListItemText primary="Prohibited: Harassment, bullying, threats, stalking, or personal attacks." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Prohibited: Encouraging dogpiling or targeted abuse." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Permitted: Constructive fitness-related criticism and respectful discussions." />
        </ListItem>
      </List>
      <Typography variant="h6">2.2 Hate Speech & Discrimination</Typography>
      <List>
        <ListItem>
          <ListItemText primary="Prohibited: Hate speech targeting various identities." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Prohibited: Slurs, dehumanizing language, or encouragement of discrimination." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Permitted: Respectful discussions on diversity, accessibility, or inclusion." />
        </ListItem>
      </List>
    </>
  ),
  "Dangerous Organizations & Extremist Content": (
    <List>
      <ListItem>
        <ListItemText primary="Prohibited: Supporting terrorist organizations or hate groups." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Prohibited: Advocating for hate groups, supremacy, or criminal enterprises." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Permitted: Academic, journalistic, or historical discussions in neutral contexts." />
      </ListItem>
    </List>
  ),
  "Fitness, Wellness, and Content Integrity": (
    <List>
      <ListItem>
        <ListItemText primary="Prohibited: Promoting unsafe fitness challenges or unverified supplements." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Required: Health-related advice must include disclaimers." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Permitted: Educational discussions on fitness with appropriate disclaimers." />
      </ListItem>
    </List>
  ),
  "Monetization, Transactions, & Fair Use": (
    <List>
      <ListItem>
        <ListItemText primary="Prohibited: Offering financial scams or unapproved transactions." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Permitted: Clearly stating pricing, perks, and refund policies." />
      </ListItem>
    </List>
  ),
  "Security, Safety & Platform Integrity": (
    <List>
      <ListItem>
        <ListItemText primary="Prohibited: Hacking, phishing, or unauthorized access." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Required: Enabling two-factor authentication (2FA) for extra security." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Permitted: Using licensed or original content with proper attribution." />
      </ListItem>
    </List>
  ),
  "Reporting Violations & Enforcement": (
    <>
      <Typography paragraph>
        Users can report violations directly through the OnlyHands platform or
        support team.
      </Typography>
      <Typography variant="h6">Reporting Process</Typography>
      <List>
        <ListItem>
          <ListItemText primary="Reports will be reviewed by the moderation team." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Consequences include warnings, suspensions, or permanent bans." />
        </ListItem>
      </List>
    </>
  ),
  Conclusion: (
    <Typography paragraph>
      These guidelines ensure OnlyHands remains a trusted and professional space
      for fitness creators and subscribers. By following these policies, you
      contribute to a safe, engaging, and high-quality fitness community.
    </Typography>
  ),
};

const Guidelines = () => {
  const [activeTab, setActiveTab] = useState("Introduction");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ display: "flex", paddingTop: 4 }}>
      {/* Tabs Section */}
      <Box
        sx={{
          width: { xs: "100%", sm: "250px" },
          marginBottom: { xs: 4, sm: 0 },
          marginTop: 2,
          marginBottom: 2,
          height: "500px",
          overflowY: "auto",
          paddingRight: 1,
          borderRight: "1px solid #ddd",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={activeTab}
          onChange={handleTabChange}
        >
          {Object.keys(guidelinesData).map((key) => (
            <Tab
              key={key}
              label={key}
              value={key}
              sx={{
                textAlign: "left",
                fontWeight: "bold",
                color: activeTab === key ? "white" : "#FC0108",
                backgroundColor: activeTab === key ? "#FC0108" : "transparent",
                border: "2px solid #FC0108",
                borderRadius: "8px",
                marginBottom: "8px",
                transition: "0.3s",
                padding: "10px 16px",
                "&.Mui-selected": { color: "white" },
                "&:hover": {
                  backgroundColor: activeTab === key ? "#FC0108" : "#ffe6e6",
                  color: activeTab === key ? "white" : "#FC0108",
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          flexGrow: 1,
          padding: 3,
          backgroundColor: "#f5f5f5",
          overflowY: "auto",
          maxHeight: "500px",
          width: "100%", // 🔥 Make sure width remains consistent
          boxSizing: "border-box", // 🔥 Prevent width jumps
        }}
      >
        {guidelinesData[activeTab]}
      </Box>
    </Container>
  );
};

export default Guidelines;
